import type { AssetId, UploadId } from '@/shared/types/common.types';
import type { StoredPart } from '@/shared/utils/upload/multipartUpload';
import type { DocumentType } from './document.types';

export const UPLOAD_STATUSES = ['Open', 'Completing', 'Completed', 'Aborted', 'Expired', 'Reused'] as const;
export type UploadStatus = (typeof UPLOAD_STATUSES)[number] | 'Unknown';

/** A stored file is usable only once verification marks it `Verified`. */
export const ASSET_STATUSES = ['Pending', 'Uploaded', 'Verifying', 'Verified', 'Rejected'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number] | 'Unknown';

/** A multipart upload Core opened in storage. It is also the engine's plan. */
export interface UploadSession {
  readonly uploadId: UploadId;
  readonly assetId: AssetId;
  readonly status: UploadStatus;
  readonly partSizeBytes: number;
  readonly partCount: number;
  /** Parts storage already holds, so a resumed upload sends only the rest. */
  readonly uploadedParts: readonly StoredPart[];
  readonly expiresAt: string;
}

export interface SignedPartUrl {
  readonly partNumber: number;
  readonly url: string;
  readonly expiresAt: string;
}

/** The stored file an upload produces; documents and revisions reference it by `assetId`. */
export interface StoredAsset {
  readonly assetId: AssetId;
  readonly status: AssetStatus;
  /** Why verification refused the file, such as `file_signature_mismatch`. Open-ended. */
  readonly rejectionCode: string | null;
  readonly fileName: string;
  readonly sizeBytes: number;
  /** Known once verification has read the file. */
  readonly detectedContentType: string | null;
  readonly sha256: string | null;
}

export interface CreateUploadInput {
  readonly fileName: string;
  readonly sizeBytes: number;
  readonly documentType: Exclude<DocumentType, 'Unknown'>;
}
